/** Stereo 70 → WGS 84, în vizualizator.
 *
 *  DE CE ÎN CLIENT. Macheta de situație are nevoie de UN SINGUR lucru din
 *  geodezie: latitudinea și longitudinea parcelei, ca să poată calcula unde e
 *  soarele. Documentul canonic le are deja — coordonatele lui sunt absolute, în
 *  Stereo 70 (x ≈ 326 000, y ≈ 583 000) — deci nu trebuie cerute de nicăieri și
 *  nu trebuie ținute minte separat. Soarele bate peste PARCELĂ, iar parcela își
 *  spune singură unde e.
 *
 *  E o A DOUA implementare a aceleiași transformări: prima e
 *  `importer/dosar/stereo70.py`, care desenează conturul pe hartă. Nu se poate
 *  reutiliza — rulează pe server, la import, iar aici trebuie să meargă în
 *  browser, pe un document deja încărcat, fără drum dus-întors. Ca să nu
 *  divergă, testul de aici folosește EXACT perechile de referință din
 *  `importer/tests/test_stereo70.py`, calculate cu PROJ.
 *
 *  Formulele sunt EPSG Guidance Note 7-2, metoda 9809 („Oblique Stereographic"),
 *  urmate de Helmert-ul EPSG:1838 în convenția Coordinate Frame (9607).
 *
 *  CÂT DE BUN E. Sub un metru pe teritoriul României față de PROJ. Pentru
 *  poziția soarelui e absurd de mult: un kilometru de eroare mută răsăritul cu
 *  vreo patru secunde. */

// --- Elipsoidul Krasovski 1940, pe care stă Stereo 70 ------------------------
const A = 6378245.0;
const F = 1.0 / 298.3;
const E2 = 2.0 * F - F * F;
const E = Math.sqrt(E2);

// --- Parametrii proiecției (EPSG:3844) ---------------------------------------
const LAT0 = (46.0 * Math.PI) / 180;
const LON0 = (25.0 * Math.PI) / 180;
const K0 = 0.99975;
const FE = 500000.0;
const FN = 500000.0;

// --- Helmert Pulkovo 1942(58) → WGS 84, varianta pentru România --------------
//
// Semnele rotațiilor sunt cele din nota de la implementarea Python, unde au
// stat greșite multă vreme și mutau parcela cu 27 m spre nord fără să strice
// forma. Dacă se umblă vreodată la ele, testul cu perechile PROJ e cel care
// prinde defectul.
const DX = 2.3287, DY = -147.0425, DZ = -92.0802;
const RX = 0.3092483, RY = -0.32482, RZ = -0.49729;   // secunde de arc
const DS = 5.68906;                                   // ppm
const SEC = Math.PI / (180.0 * 3600.0);

// --- Constantele sferei conforme, o dată pentru totdeauna --------------------
const SIN0 = Math.sin(LAT0);
const NU0 = A / Math.sqrt(1.0 - E2 * SIN0 * SIN0);
const RHO0 = (A * (1.0 - E2)) / Math.pow(1.0 - E2 * SIN0 * SIN0, 1.5);
const R = Math.sqrt(RHO0 * NU0);
const N = Math.sqrt(1.0 + (E2 * Math.pow(Math.cos(LAT0), 4)) / (1.0 - E2));
const W1 = Math.pow(
  ((1.0 + SIN0) / (1.0 - SIN0)) * Math.pow((1.0 - E * SIN0) / (1.0 + E * SIN0), E), N);
const SIN_CHI0 = (W1 - 1.0) / (W1 + 1.0);
const C = ((N + SIN0) * (1.0 - SIN_CHI0)) / ((N - SIN0) * (1.0 + SIN_CHI0));
const CHI0 = Math.asin((C * W1 - 1.0) / (C * W1 + 1.0));

// --- Elipsoidul WGS 84 -------------------------------------------------------
const A_WGS = 6378137.0;
const F_WGS = 1.0 / 298.257223563;
const E2_WGS = 2.0 * F_WGS - F_WGS * F_WGS;

/** (nord, est) în metri → (lat, lon) în radiani, pe elipsoidul Krasovski. */
function stereoLaGeo(nord: number, est: number): [number, number] {
  const dE = est - FE;
  const dN = nord - FN;
  const g = 2.0 * R * K0 * Math.tan(Math.PI / 4.0 - CHI0 / 2.0);
  const h = 4.0 * R * K0 * Math.tan(CHI0) + g;
  const i = Math.atan2(dE, h + dN);
  const j = Math.atan2(dE, g - dN) - i;
  const chi = CHI0 + 2.0 * Math.atan((dN - dE * Math.tan(j / 2.0)) / (2.0 * R * K0));
  const lon = (j + 2.0 * i + LON0 - LON0) / N + LON0;

  // Latitudinea geodezică din cea conformă: latitudinea izometrică se
  // inversează iterativ. Converge în trei-patru pași; limita e ca bucla să nu
  // poată fi infinită pe o intrare absurdă.
  const psi = (0.5 * Math.log((1.0 + Math.sin(chi)) / (C * (1.0 - Math.sin(chi))))) / N;
  let lat = 2.0 * Math.atan(Math.exp(psi)) - Math.PI / 2.0;
  for (let k = 0; k < 12; k++) {
    const s = Math.sin(lat);
    const psiI = Math.log(
      Math.tan(lat / 2.0 + Math.PI / 4.0) * Math.pow((1.0 - E * s) / (1.0 + E * s), E / 2.0));
    const d = (-(psiI - psi) * Math.cos(lat) * (1.0 - E2 * s * s)) / (1.0 - E2);
    lat += d;
    if (Math.abs(d) < 1e-12) break;
  }
  return [lat, lon];
}

function geoLaEcef(lat: number, lon: number, a: number, e2: number): [number, number, number] {
  const s = Math.sin(lat), c = Math.cos(lat);
  const nu = a / Math.sqrt(1.0 - e2 * s * s);
  return [nu * c * Math.cos(lon), nu * c * Math.sin(lon), nu * (1.0 - e2) * s];
}

function ecefLaGeo(x: number, y: number, z: number, a: number, e2: number): [number, number] {
  const lon = Math.atan2(y, x);
  const p = Math.hypot(x, y);
  let lat = Math.atan2(z, p * (1.0 - e2));
  for (let k = 0; k < 12; k++) {
    const s = Math.sin(lat);
    const nu = a / Math.sqrt(1.0 - e2 * s * s);
    const h = p / Math.cos(lat) - nu;
    const nou = Math.atan2(z, p * (1.0 - (e2 * nu) / (nu + h)));
    const gata = Math.abs(nou - lat) < 1e-13;
    lat = nou;
    if (gata) break;
  }
  return [lat, lon];
}

/** Coordinate Frame (EPSG 9607), cei șapte parametri. */
function helmert(x: number, y: number, z: number): [number, number, number] {
  const rx = RX * SEC, ry = RY * SEC, rz = RZ * SEC;
  const s = 1.0 + DS * 1e-6;
  return [DX + s * (x + rz * y - ry * z),
          DY + s * (-rz * x + y + rx * z),
          DZ + s * (ry * x - rx * y + z)];
}

/** Un punct Stereo 70 → (lat, lon) în GRADE.
 *
 *  Ordinea argumentelor e cea din extrasul de carte funciară, unde coloanele
 *  se scriu `X` (nord) și `Y` (est) — cu alte nume, ca să nu se poată inversa
 *  în tăcere. */
export function laWgs84(nord: number, est: number): { lat: number; lon: number } {
  const [lat0, lon0] = stereoLaGeo(nord, est);
  const [x, y, z] = helmert(...geoLaEcef(lat0, lon0, A, E2));
  const [lat, lon] = ecefLaGeo(x, y, z, A_WGS, E2_WGS);
  return { lat: (lat * 180) / Math.PI, lon: (lon * 180) / Math.PI };
}

/** Cât de departe de România poate cădea un punct înainte să nu mai fie
 *  crezut. Documentele neorientate au coordonate locale (0…60 m), iar trecute
 *  prin proiecție ies în Marea Neagră sau în Ucraina — deci rezultatul se
 *  verifică, nu se folosește orbește. */
export const ROMANIA = { latMin: 43.5, latMax: 48.4, lonMin: 20.2, lonMax: 29.8 };

export function inRomania(p: { lat: number; lon: number }): boolean {
  return p.lat >= ROMANIA.latMin && p.lat <= ROMANIA.latMax
      && p.lon >= ROMANIA.lonMin && p.lon <= ROMANIA.lonMax;
}
